import React from 'react';
import { Link } from 'react-router-dom';
export type BlogStep = {
  number: number;
  title: string;
  image: string;
  description: string;
  tips: string[];
  material?: string;
  materialSlug?: string;
};
export type BlogPost = {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  type: 'guide' | 'article';
  quickInfo?: string[];
  materialsNeeded?: {
    name: string;
    use: string;
    slug: string | null;
  }[];
  steps?: BlogStep[];
  content?: React.ReactNode;
  /**
   * Plain-text Q&A pairs. Rendered at the foot of the post AND emitted as
   * FAQPage structured data, which is where most of the search value sits —
   * without the markup Google cannot read these as question/answer pairs.
   * Plain strings only, so the schema and the visible copy stay identical.
   */
  faqs?: {
    q: string;
    a: string;
  }[];
  seoBlocks?: {
    title: string;
    content: React.ReactNode;
  }[];
};
export const blogPosts: BlogPost[] = [{
  slug: 'fix-muddy-gravel-driveway-fall-rains',
  title: 'How to Fix a Muddy Gravel Driveway Before the Fall Rains',
  date: 'August 18, 2026',
  excerpt: 'Puget Sound driveways do not fail because they ran out of gravel — they fail because water got underneath them. Here is how to tell a surface problem from a dead base, what to order for each, and why doing it in September costs half what it costs in December.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/b71bc766-d0e3-4639-b9c7-139003882a3b.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1991" data-mp="326r95uzw9vb3">
        <p className="mb-4" data-id="element-1992" data-mp="2niecevszg8fj">
          Every year around the third week of October our phone starts ringing
          with the same call. A driveway in Maple Valley or Graham held up fine
          all summer, the first real atmospheric river came through, and now
          there are ruts deep enough to scrape a bumper and a soft spot near the
          garage that swallows a wheel.
        </p>
        <p className="mb-4" data-id="element-1993" data-mp="3ut960b06hmyf">
          We can deliver rock into that. But the honest version, which most
          suppliers will not tell you because it costs them a load: dumping
          gravel on a wet, failed driveway in November mostly buys you the
          privilege of doing it again in March. The same repair done on dry
          ground in September uses about half the material and lasts years
          instead of weeks.
        </p>
        <p className="mb-4" data-id="element-1994" data-mp="373y1qjjmdafj">
          <strong data-id="element-1995" data-mp="373oaksmu8x89">The short version:</strong> figure out whether you have a
          surface problem or a base failure, fix the water before you buy any
          rock, then build back with{' '}
          <Link to="/shop/1-25-minus" className="underline underline-offset-2" data-id="element-1996" data-mp="2x7bks5uez3is">
            1.25" Minus
          </Link>{' '}
          for structure and{' '}
          <Link to="/shop/5-8-minus" className="underline underline-offset-2" data-id="element-1997" data-mp="2o5lu528cw8m2">
            5/8" Minus
          </Link>{' '}
          for the driving surface. Everything below is the detail behind those
          three steps.
        </p>

        <figure className="my-8" data-id="element-1998" data-mp="18ll6jlchzl6i">
          <img src="https://cdn.magicpatterns.com/uploads/j1nBk664KPmDQhcF58B5oe/muddy_driveway_fix_0.png" alt="Split view of the same driveway: rutted mud and standing water on the left, a freshly delivered and graded crushed rock surface on the right, with a dump truck placing material between them" className="w-full" loading="lazy" data-id="element-1999" data-mp="18oquuydayj16" />
          <figcaption className="mt-2 text-xs text-stone-600 dark:text-stone-400" data-id="element-2000" data-mp="oy5alicaemaw">
            The same driveway either side of a rebuild. The difference is not
            how much rock went down — it is what was underneath it first.
          </figcaption>
        </figure>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2001" data-mp="yqazgj78snlv">
          Why Puget Sound Driveways Turn to Soup
        </h3>
        <p className="mb-4" data-id="element-2002" data-mp="66unzddlgvot">
          Two local facts do almost all the damage here. First, we get roughly
          37 inches of rain a year and most of it arrives between October and
          March, in long soaking stretches rather than short storms. The ground
          never gets a chance to dry out between events. Second, much of the
          native soil under our driveways is glacial till or silty clay — dense,
          slow-draining material that turns plastic when it is saturated.
        </p>
        <p className="mb-4" data-id="element-2003" data-mp="2rjr46m9d651v">
          Plastic subgrade is the whole problem. Rock sitting on firm ground
          spreads a tire load out across a wide area. Rock sitting on saturated
          till gets pumped down into it every time a vehicle passes — the load
          pushes stone into the mud, mud squeezes up into the voids between the
          stone, and within a few weeks you have a layer that is neither rock
          nor soil and holds nothing. That is what people are describing when
          they say the gravel "disappeared." It did not wash away. It sank, and
          the mud came up to meet it.
        </p>

        <figure className="my-8" data-id="element-2004" data-mp="s4th5prspkkc">
          <img src="https://cdn.magicpatterns.com/uploads/mbhgmTdkcynGse64Wzt23y/muddy_driveway_fix_3.png" alt="Cutaway of a rutted gravel driveway in the rain, with water draining down through the rock layer into saturated mud below and the surface sagging into wheel tracks" className="w-full" loading="lazy" data-id="element-2005" data-mp="w4an0kb4wjq1" />
          <figcaption className="mt-2 text-xs text-stone-600 dark:text-stone-400" data-id="element-2006" data-mp="8dexxuy2c2mh">
            Base failure in progress: rock pumped down into saturated subgrade,
            ruts holding water, and no compacted structure left to carry a
            tire load.
          </figcaption>
        </figure>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2007" data-mp="4dwdf0a5ltk6">
          Step 1: Diagnose It Honestly — Surface or Base?
        </h3>
        <p className="mb-4" data-id="element-2008" data-mp="1yqw3jnc5u65p">
          This is the only decision that really matters, because the two
          problems need different materials and different money. Two field
          tests, both free:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2009" data-mp="1sa48xs93uhb2">
          <li data-id="element-2010" data-mp="1scqg3qs32usr">
            <strong data-id="element-2011" data-mp="1nbj8brpxxvm3">The flex test.</strong> Have someone drive slowly past while
            you stand to the side and watch the ground next to the tire. If the
            surface visibly bows or ripples ahead of and behind the wheel, the
            base is gone. A sound driveway does not move.
          </li>
          <li data-id="element-2012" data-mp="2b1dks86cli5t">
            <strong data-id="element-2013" data-mp="2b0lqr1sefshn">The rebar test.</strong> Push a length of rebar or a long
            screwdriver into the driveway by hand. If it slides in past 4 or 5
            inches with light pressure, there is no compacted structure left
            under you — you are standing on loose fill over mud.
          </li>
        </ul>
        <div className="overflow-x-auto my-6" data-id="element-2014" data-mp="2yrhlo7s5j3t4">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-2015" data-mp="2tzzlhlxyd797">
            <thead data-id="element-2016" data-mp="2txwnaemnc7te">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-2017" data-mp="31b5nj27f9ltu">
                <th className="py-2 pr-4 font-bold" data-id="element-2018" data-mp="31d8nof2agvca" />
                <th className="py-2 pr-4 font-bold" data-id="element-2019" data-mp="36ba38q0iacj0">Surface problem</th>
                <th className="py-2 font-bold" data-id="element-2020" data-mp="3b9lbwxf2egzn">Base failure</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-2021" data-mp="3g853bdcfgkze">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2022" data-mp="3karxvm0hgk0a">
                <td className="py-2 pr-4 font-semibold" data-id="element-2023" data-mp="3p8tfe2i9gb9n">What you see</td>
                <td className="py-2 pr-4" data-id="element-2024" data-mp="3p7060fg9jcxy">
                  Thin, faded rock; shallow washboarding; puddles that drain
                </td>
                <td className="py-2" data-id="element-2025" data-mp="2u1qulojs4vo">
                  Ruts that refill after grading; ground flexes; standing water
                  that never leaves
                </td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2026" data-mp="btopx0tt0ylk">
                <td className="py-2 pr-4 font-semibold" data-id="element-2027" data-mp="x553tqbb5x7m">Rebar test</td>
                <td className="py-2 pr-4" data-id="element-2028" data-mp="x3bws9ws9az3">Stops firm in 1–2 inches</td>
                <td className="py-2" data-id="element-2029" data-mp="t2i9nir60079">Sinks 5+ inches by hand</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2030" data-mp="173kw3wm2ncip">
                <td className="py-2 pr-4 font-semibold" data-id="element-2031" data-mp="124hoq5imcu7k">What to order</td>
                <td className="py-2 pr-4" data-id="element-2032" data-mp="12257jukoqnvk">5/8" Minus, 1–2 inches</td>
                <td className="py-2" data-id="element-2033" data-mp="effaz8r3vlic">
                  1.25" Minus base, then 5/8" Minus surface
                </td>
              </tr>
              <tr data-id="element-2034" data-mp="9dpydoo8izs5">
                <td className="py-2 pr-4 font-semibold" data-id="element-2035" data-mp="o46z15t1plnz">
                  Typical 1,000 sq ft order
                </td>
                <td className="py-2 pr-4" data-id="element-2036" data-mp="jdgvly46jsvx">3–6 yards</td>
                <td className="py-2" data-id="element-2037" data-mp="jbx9hqvuenm8">12–18 yards</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4" data-id="element-2038" data-mp="3muqjjtpewubn">
          If it is genuinely just a surface problem, stop reading and go to our{' '}
          <Link to="/projects/driveway-top-off" className="underline underline-offset-2" data-id="element-2039" data-mp="22c6b6fqp515b">
            driveway top-off guide
          </Link>{' '}
          — you need far less rock than most people order, and ordering base
          depth for a top-off is the single most common way people overspend
          with us. The rest of this article is about base failure.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2040" data-mp="2l35v32jc3krq">
          Step 2: Fix the Water First, or Skip Straight to Doing It Twice
        </h3>
        <p className="mb-4" data-id="element-2041" data-mp="2pzmuisvzhlk4">
          Mud is a symptom. Water is the disease. If water is still arriving
          under your driveway next February, no combination of materials will
          hold, and we would rather tell you that now than sell you 15 yards of
          rock that will be gone by spring. Walk the driveway during actual
          rain — not after — and find where the water comes from and where it
          goes.
        </p>

        <figure className="my-8" data-id="element-2042" data-mp="6i3ou3jz811d">
          <img src="https://cdn.magicpatterns.com/uploads/8y2xF5eSv8nR916T4rn99P/muddy_driveway_fix_1.png" alt="Gravel driveway with a raised centre crown shedding rainwater sideways into open shoulder ditches on both edges" className="w-full" loading="lazy" data-id="element-2043" data-mp="6j58gwf25vup" />
          <figcaption className="mt-2 text-xs text-stone-600 dark:text-stone-400" data-id="element-2044" data-mp="1qtx8xevanmu">
            What you are aiming for: about 2 inches of crown across a 10-foot
            width, with open shoulders so water leaves sideways instead of
            soaking straight down.
          </figcaption>
        </figure>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2045" data-mp="3a03xlkxcwag1">
          <li data-id="element-2046" data-mp="3ey5hu96iqft3">
            <strong data-id="element-2047" data-mp="3exnaohibih6e">Re-establish the crown.</strong> The center should sit about
            2 inches higher than the edges across a 10-foot width. A flat
            driveway holds water on its surface; a crowned one sheds it sideways
            in seconds. This one change does more for longevity than an extra
            inch of rock.
          </li>
          <li data-id="element-2048" data-mp="1nayywetv6xpv">
            <strong data-id="element-2049" data-mp="1n8csiiipmo98">Open the shoulders.</strong> Over years, grass, moss, and
            grade build a berm along both edges that turns the driveway into a
            bathtub. Cut it back so water has somewhere to leave.
          </li>
          <li data-id="element-2050" data-mp="1rbsrhli950p3">
            <strong data-id="element-2051" data-mp="1rb9f0kbdf4ig">Move roof water away.</strong> A single downspout dumping
            near a driveway edge can saturate the subgrade for 20 feet in every
            direction. Pipe it out past the shoulder or into a proper trench.
          </li>
          <li data-id="element-2052" data-mp="214hv5u7k9a9v">
            <strong data-id="element-2053" data-mp="2f1n0tt3d474l">Deal with springs and seeps.</strong> If a section stays wet
            in August, groundwater is moving through it and needs a French drain
            wrapped in fabric with{' '}
            <Link to="/shop/7-8-drain-rock" className="underline underline-offset-2" data-id="element-2054" data-mp="knm0t4w2te2x">
              7/8" Drain Rock
            </Link>
            , not surface gravel. Use a clean rock there — a minus would seal up
            and defeat the purpose.
          </li>
          <li data-id="element-2055" data-mp="2ft4ymgabv55q">
            <strong data-id="element-2056" data-mp="2ftelvw3zmy7q">Check the culvert.</strong> If your driveway crosses a
            ditch, a plugged culvert backs water up under the whole approach.
            Clear it before the leaves come down.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2057" data-mp="2uouedwox6m20">
          Step 3: Rebuild It So It Stays Fixed
        </h3>
        <p className="mb-4" data-id="element-2058" data-mp="2ypmoes5m8uzc">
          This is the sequence we would use on our own place, in order. You can
          do all of it with a rented skid steer and a plate compactor over a
          long weekend.
        </p>

        <figure className="my-8" data-id="element-2059" data-mp="3destuxv1rhfo">
          <img src="https://cdn.magicpatterns.com/uploads/uTbW2mvcmmDfVSpW3jS7RP/muddy_driveway_fix_2.png" alt="Cutaway of a rebuilt driveway showing geotextile fabric over dark subgrade, a coarse 1.25 inch minus base layer, a finer 5 eighths minus surface layer, and a plate compactor on top" className="w-full" loading="lazy" data-id="element-2060" data-mp="3df2dm3pi0scx" />
          <figcaption className="mt-2 text-xs text-stone-600 dark:text-stone-400" data-id="element-2061" data-mp="1atggwqx4kthq">
            The build, bottom to top: separation fabric over the subgrade, 4
            inches of 1.25" Minus compacted in two lifts, then 2–3 inches of
            5/8" Minus as the driving surface.
          </figcaption>
        </figure>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2062" data-mp="wup7hht7jvzx">
          <li data-id="element-2063" data-mp="1uet9z9b344r7">
            <strong data-id="element-2064" data-mp="1ufl38dh7lqeb">1. Get the pumping mud out of the way.</strong> Strip off
            the contaminated rock-and-mud mix rather than burying it. Anything
            that squelches will keep squelching under a new layer.
          </li>
          <li data-id="element-2065" data-mp="1fr86vfe7m5ja">
            <strong data-id="element-2066" data-mp="1frqch2mrhw3v">2. Lay geotextile fabric.</strong> This is the cheapest and
            most skipped step in the entire job. Non-woven separation fabric
            costs a few hundred dollars for a driveway and stops the mud below
            from ever mixing with the rock above. On soft ground it easily
            doubles the life of the repair. Buy it from any landscape supply or
            builder's yard — we do not sell it, and we will still tell you to
            use it.
          </li>
          <li data-id="element-2067" data-mp="1j0828tblh5gg">
            <strong data-id="element-2068" data-mp="1j0qaypfg1c59">3. If the ground will not hold a truck, start with pit
            run.</strong> On genuinely saturated sites, place 6 or more inches
            of{' '}
            <Link to="/shop/pit-run" className="underline underline-offset-2" data-id="element-2069" data-mp="2bom5polgctze">
              Pit Run
            </Link>{' '}
            at $37/yd as a working platform. It is the cheapest way to build up
            out of mud, and putting $65 rock down there instead is money buried
            for no gain.
          </li>
          <li data-id="element-2070" data-mp="26wlxz9z54ws9">
            <strong data-id="element-2071" data-mp="26wcfe7gfdrwl">4. Build the structural base with 1.25" Minus.</strong> Four
            inches, placed and compacted in two lifts of about 2 inches each.
            The large angular pieces interlock and carry the load; the fines
            lock them together. This layer — called CSBC on county plans — is
            what actually makes a driveway a driveway.
          </li>
          <li data-id="element-2072" data-mp="iytkdrv2aike">
            <strong data-id="element-2073" data-mp="iz37n7oq2bme">5. Top with 2–3 inches of 5/8" Minus.</strong> Compacted in
            one pass. This is your driving and walking surface: tight, smooth,
            and shedding water rather than absorbing it.
          </li>
          <li data-id="element-2074" data-mp="srshxz8jdjg5">
            <strong data-id="element-2075" data-mp="503ougnw4qy5">6. Compact wet, but not soaked.</strong> Minus rock needs
            some moisture for the fines to bind. A light mist from a hose before
            the compactor is ideal. Compacting bone-dry rock never sets it up;
            compacting flooded rock just pushes it into the subgrade.
          </li>
        </ul>
        <p className="mb-4" data-id="element-2076" data-mp="90nivbd63eg2">
          Rebuilding a soft section is exactly what our{' '}
          <Link to="/projects/muddy-driveway-fix" className="underline underline-offset-2" data-id="element-2077" data-mp="1pf5m22x3eikq">
            muddy driveway fix guide
          </Link>{' '}
          walks through material by material, with the depths already worked
          out.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2078" data-mp="i5k3hmwqx0qc">
          How Much to Order
        </h3>
        <p className="mb-4" data-id="element-2079" data-mp="r4dvezz4t2xk">
          Yardage is length × width × depth ÷ 324, and it is the number people
          get wrong in both directions. A full rebuild on a typical suburban
          driveway is more rock than most homeowners expect; a top-off is far
          less. Here are real numbers for a 12-foot-wide drive:
        </p>
        <div className="overflow-x-auto my-6" data-id="element-2080" data-mp="3fcoxi9v01ux">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-2081" data-mp="8dmkhtzhx4h2">
            <thead data-id="element-2082" data-mp="8bjqyzz4wt7l">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-2083" data-mp="d4311mym567n">
                <th className="py-2 pr-4 font-bold" data-id="element-2084" data-mp="d67f2xq87lln">Driveway</th>
                <th className="py-2 pr-4 font-bold" data-id="element-2085" data-mp="3btle0gd6aaic">Top-off (2")</th>
                <th className="py-2 font-bold" data-id="element-2086" data-mp="3gsxwkox0gvf4">Full rebuild (4" + 3")</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-2087" data-mp="39a6s0gp8gj9l">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2088" data-mp="34c5bo3j6yy1t">
                <td className="py-2 pr-4 font-semibold" data-id="element-2089" data-mp="2zazhe19vv2q6">12 × 40 ft (480 sq ft)</td>
                <td className="py-2 pr-4" data-id="element-2090" data-mp="2vap0vuogstx2">~3 yd</td>
                <td className="py-2" data-id="element-2091" data-mp="2v9w33j7hher2">~6 yd base + ~4.5 yd surface</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2092" data-mp="2qcnjde6ot2np">
                <td className="py-2 pr-4 font-semibold" data-id="element-2093" data-mp="2ljb6g1bdekas">12 × 80 ft (960 sq ft)</td>
                <td className="py-2 pr-4" data-id="element-2094" data-mp="2gkq6m30162mv">~6 yd</td>
                <td className="py-2" data-id="element-2095" data-mp="2glj7incb5xx3">~12 yd base + ~9 yd surface</td>
              </tr>
              <tr data-id="element-2096" data-mp="2bnrefpzxg5rb">
                <td className="py-2 pr-4 font-semibold" data-id="element-2097" data-mp="2bk3larye9ld4">
                  12 × 150 ft (1,800 sq ft)
                </td>
                <td className="py-2 pr-4" data-id="element-2098" data-mp="22lqgss0s7hp4">~11 yd</td>
                <td className="py-2" data-id="element-2099" data-mp="22m07khtnhsvv">~22 yd base + ~17 yd surface</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4" data-id="element-2100" data-mp="3vh99a0jvd00c">
          Add roughly 15% for compaction — a 4-inch compacted layer starts life
          closer to 4.75 inches loose. Our{' '}
          <Link to="/calculator" className="underline underline-offset-2" data-id="element-2101" data-mp="3hgyhmvaqjhe4">
            yardage calculator
          </Link>{' '}
          handles the math and the depth defaults per project type if you would
          rather not do it on paper.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2102" data-mp="3cix04esyjq4r">
          What It Costs Delivered
        </h3>
        <p className="mb-4" data-id="element-2103" data-mp="2xutq8ubsnuao">
          Both 1.25" Minus and 5/8" Minus are $65 per yard. Delivery is a flat
          fee per truckload by zone — $80, $120, or $175 — and that fee is
          identical whether the truck carries 3 yards or a full 12. Partial
          loads are therefore the expensive way to buy rock, and the fix is
          usually to order the base and surface as full loads rather than
          topping up twice.
        </p>
        <p className="mb-4" data-id="element-2104" data-mp="2570vgv4qxp45">
          A 960 sq ft rebuild is about 21 yards of material, or two truckloads.
          A pit run platform underneath adds a third. If you are close to
          filling a truck, round up — the marginal yard is cheap and the second
          delivery fee is not.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2105" data-mp="2t4q35yvw5ui5">
          The Do's
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2106" data-mp="2o6ohr7aztj3i">
          <li data-id="element-2107" data-mp="2o42al8s0l5lt">
            <strong data-id="element-2108" data-mp="32ujcsuch3zjr">Do it before the ground saturates.</strong> Late August
            through September is the window. Early October is usually still
            workable; by mid-November the subgrade is plastic and you are
            fighting physics.
          </li>
          <li data-id="element-2109" data-mp="3btei847p15m0">
            <strong data-id="element-2110" data-mp="3bt4u6m67l8iy">Do use fabric on soft ground.</strong> Cheapest insurance in
            the whole project.
          </li>
          <li data-id="element-2111" data-mp="3jel5pdrek7e9">
            <strong data-id="element-2112" data-mp="3je31nuyi0ovs">Do compact in lifts.</strong> Two 2-inch passes beat one
            4-inch dump every single time. A plate compactor's energy does not
            reach the bottom of a deep loose layer.
          </li>
          <li data-id="element-2113" data-mp="3tcs9sw0leuyu">
            <strong data-id="element-2114" data-mp="3tb7qy3nn50gt">Do build a crown and keep the shoulders open.</strong> Free,
            and it is the difference between a repair that lasts five years and
            one that lasts one.
          </li>
          <li data-id="element-2115" data-mp="30mk25y3onsyi">
            <strong data-id="element-2116" data-mp="2vpjvbkz0em6c">Do stage the drop.</strong> Have the truck put the rock at
            the far end and work back toward the road so you are never spreading
            across finished surface.
          </li>
          <li data-id="element-2117" data-mp="35nzk3hakdjco">
            <strong data-id="element-2118" data-mp="35ng260kwyuyn">Do order full truckloads.</strong> The haul fee is per load,
            not per yard.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2119" data-mp="2hwkg7k352tzb">
          The Don'ts
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2120" data-mp="2cx7tr0g6jox9">
          <li data-id="element-2121" data-mp="sfyl9b7sgjcr">
            <strong data-id="element-2122" data-mp="sfgjjz2cxcxg">Do not use clean or washed rock as a driving
            surface.</strong> Pea gravel, 5/8" Clean, and drain rock have no
            fines, so they never lock together. They migrate under tires, spread
            into the lawn, and are miserable to walk on. Clean rock belongs in
            drains, not driveways.
          </li>
          <li data-id="element-2123" data-mp="1b7r5ag4vqu8c">
            <strong data-id="element-2124" data-mp="1b79alnxv8o2j">Do not dump new rock over pumping mud.</strong> Without
            excavation and fabric, you are burying good material in bad. This is
            the number one reason people buy the same driveway twice.
          </li>
          <li data-id="element-2125" data-mp="126jj2wfn1jum">
            <strong data-id="element-2126" data-mp="1295pgsqsltb9">Do not fill potholes without squaring the edges.</strong>{' '}
            Loose rock in a smooth dish works right back out. Cut the hole
            square so the patch keys in, fill with 1.25" Minus, compact, then
            top.
          </li>
          <li data-id="element-2127" data-mp="3rwwl1k1g92c9">
            <strong data-id="element-2128" data-mp="3rxg639mec6ui">Do not treat a water problem as a gravel problem.</strong>{' '}
            If a downspout, ditch, or seep is feeding the failure, rock is a
            temporary cover over an unfixed cause.
          </li>
          <li data-id="element-2129" data-mp="1nq3khw16x36f">
            <strong data-id="element-2130" data-mp="1npv8058zbzvr">Do not use hog fuel or bark where vehicles drive.</strong>{' '}
            <Link to="/shop/hog-fuel" className="underline underline-offset-2" data-id="element-2131" data-mp="1joi07pmbtmm7">
              Hog fuel
            </Link>{' '}
            is excellent for paddocks, trails, and dog runs, and it composts
            into mush under tires.
          </li>
          <li data-id="element-2132" data-mp="19pkfyyvxof90">
            <strong data-id="element-2133" data-mp="19p0ux9azlaqr">Do not have a 12-yard truck drive onto saturated
            ground.</strong> A loaded dump truck weighs about 30 tons and will
            rut your yard worse than the problem you are fixing. Plan the drop
            spot from firm ground.
          </li>
          <li data-id="element-2134" data-mp="2z48nomtkx90p">
            <strong data-id="element-2135" data-mp="2z4qkplo2fr9o">Do not order a 2-yard load in your pickup.</strong> A single
            yard of minus is roughly 2,800 lbs. Two yards is over the payload of
            nearly every half-ton truck on the road.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2136" data-mp="lv0ixsyflumm">
          If You Have Already Missed the Window
        </h3>
        <p className="mb-4" data-id="element-2137" data-mp="qn0hbgyusfh3">
          It happens, and there is a right way to triage. Do not attempt a full
          rebuild in standing water — you will spend base-layer money for
          subgrade results. Instead, get through winter: pull the water off with
          a shovel-cut channel and open shoulders, put{' '}
          <Link to="/shop/pit-run" className="underline underline-offset-2" data-id="element-2138" data-mp="2y8tt2w7klan">
            Pit Run
          </Link>{' '}
          or{' '}
          <Link to="/shop/4-8-quarry-spalls" className="underline underline-offset-2" data-id="element-2139" data-mp="bxccw6vdl0p5">
            4–8" Quarry Spalls
          </Link>{' '}
          into the worst soft spots to get a driveable platform, and plan the
          real rebuild for the first dry stretch. Spalls are large enough that
          they do not pump down into mud the way smaller rock does, which is why
          they are the standard construction-entrance material on wet sites.
        </p>
        <p className="mb-4" data-id="element-2140" data-mp="1gxu0yw3mjzur">
          That is a holding action, not a fix. But it is a far better use of
          $500 in December than a base rebuild that fails in March.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2141" data-mp="1qsm1a7old0t8">
          The Bottom Line
        </h3>
        <p className="mb-4" data-id="element-2142" data-mp="1zuliazzlw4w6">
          Diagnose before you buy. Fix the water before you buy. Then build
          1.25" Minus for structure and 5/8" Minus for surface, compacted in
          lifts over fabric. Done in September on dry glacial till, that
          driveway will carry loaded trucks through several winters. Done in
          December on saturated ground, the same order is a down payment on
          doing it again.
        </p>
        <p className="mb-4" data-id="element-2143" data-mp="2imebaueodq3w">
          If you are not sure which category you are in, text dispatch a photo
          of the worst section and your rough dimensions. We will tell you
          honestly whether you need 6 yards or 20 — including when the answer is
          that you do not need a delivery at all yet.
        </p>
      </div>,
  faqs: [{
    q: 'What is the best gravel for a muddy driveway?',
    a: 'For a driveway that has gone soft, 1.25" Minus is the base material and 5/8" Minus is the surface. Both are crushed rock with fines left in, so they compact into a solid layer. On genuinely saturated ground, place Pit Run first as a working platform, and use 4-8" Quarry Spalls in the worst spots. Never use clean or washed rock like pea gravel as a driving surface — it has no fines and never locks together.'
  }, {
    q: 'Can I just put gravel over mud?',
    a: 'Not if you want it to last. Rock placed directly on saturated subgrade gets pumped down into the mud by traffic while mud squeezes up into the rock, and within weeks the layer holds nothing. Strip the contaminated material, lay non-woven geotextile separation fabric, then build back with compacted rock. The fabric is the cheapest step in the job and the most commonly skipped.'
  }, {
    q: 'How do I know if my driveway base has failed or just needs more rock?',
    a: 'Two tests. Have someone drive past slowly and watch the ground beside the tire — if the surface visibly bows or ripples, the base is gone. Then push a length of rebar into the driveway by hand; if it slides in past 4 or 5 inches, there is no compacted structure left. A surface problem needs 1-2 inches of 5/8" Minus. A base failure needs 4 inches of 1.25" Minus under a 2-3 inch surface layer.'
  }, {
    q: 'How much gravel do I need to fix a muddy driveway?',
    a: 'A full rebuild on a 12 x 80 ft driveway (960 sq ft) takes about 12 yards of 1.25" Minus for a 4-inch base plus about 9 yards of 5/8" Minus for a 3-inch surface. Add roughly 15% for compaction. A simple top-off on the same driveway is only 3-6 yards.'
  }, {
    q: 'When is the best time to fix a gravel driveway in the Seattle area?',
    a: 'Late August through September. Crushed minus rock only compacts properly within a fairly narrow moisture range, and by mid-November the glacial till and silty clay under most Puget Sound driveways is saturated and plastic. The same repair in winter takes more material and delivers a worse result.'
  }, {
    q: 'Does geotextile fabric really make a difference under gravel?',
    a: 'On soft ground, yes — it commonly doubles the life of the repair. Non-woven separation fabric stops the mud below from mixing with the rock above, which is the actual failure mechanism in most Puget Sound driveways. It costs a few hundred dollars for a typical driveway. We do not sell it, and we still recommend it on every soft-ground job.'
  }, {
    q: 'Will crushed rock fix standing water on my driveway?',
    a: 'No. Standing water that never drains is a grading or groundwater problem, and gravel placed over it will fail again. Re-establish a 2-inch crown across a 10-foot width, cut back the berms along the shoulders so water can leave, pipe downspouts past the driveway, and install a fabric-wrapped French drain with 7/8" Drain Rock where groundwater is moving through.'
  }]
}, {
  slug: '5-8-minus-gravel-guide',
  title: '5/8" Minus Gravel: Weight, Coverage, and How to Install It',
  date: 'August 7, 2026',
  excerpt: 'It is the single most-ordered material we haul in King and Pierce counties. Here is what a yard weighs, how far it goes, where it belongs, and the install mistakes that cost people a second delivery.',
  image: 'https://cdn.magicpatterns.com/uploads/r4cKQMZPXB8R1pGgK5XvGj/5-8-minus-newcastle.png',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2144" data-mp="3gugpnsck05l8">
        <p className="mb-4" data-id="element-2145" data-mp="112sx094koz88">
          More than a third of the loads we dispatch on a given week are 5/8"
          Minus. Contractors order it by the truck without thinking twice.
          Homeowners call and describe it as "the gravel that packs down hard,"
          which is exactly right — that is the whole point of the material.
        </p>
        <p className="mb-4" data-id="element-2146" data-mp="uox50fob8n8d">
          <strong data-id="element-2147" data-mp="upydccdxyvme">The short version:</strong> 5/8" Minus is crushed rock
          screened so nothing is larger than 5/8 of an inch, with all the fines
          from crushing left in. Those fines pack into the gaps between the
          angular pieces and lock the whole layer into a near-solid slab. One
          cubic yard weighs about 2,800 lbs and covers roughly 100 sq ft at 3
          inches deep. It runs $65 per yard delivered from our yard, plus a flat
          haul fee by zone.
        </p>

        <img src="https://cdn.magicpatterns.com/uploads/uJHdEMdk8Ku5nMisWk411s/58minus.png" alt="Close-up of 5/8 inch minus crushed gravel showing angular stone mixed with fines" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2148" data-mp="1ikro9v4z6idj" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2149" data-mp="3d2xmtft8zss5">
          What "Minus" Actually Means
        </h3>
        <p className="mb-4" data-id="element-2150" data-mp="3i0fyvf2kao4d">
          Take quarry rock, run it through a crusher, then screen out everything
          bigger than 5/8 of an inch — about the width of a nickel. What is left
          is every size below that, down to the rock dust the crusher produced.
          The dust is the "minus," and it is the reason the material works.
        </p>
        <p className="mb-4" data-id="element-2151" data-mp="2z2mv6ak46a5x">
          Run a compactor over it and those fines migrate into every void
          between the larger angular chips. The layer stops behaving like loose
          stone and starts behaving like a slab. Clean rock — the same stone with
          the fines washed out — never does that. It stays loose forever, which
          is excellent if you want water to pour through it and useless if you
          want to drive on it.
        </p>
        <p className="mb-4" data-id="element-2152" data-mp="nbiz9tsso5ej">
          One rule covers almost every ordering decision:{' '}
          <strong data-id="element-2153" data-mp="27ulain8ohb1r">
            if it needs to be solid, order a minus. If it needs to drain, order
            a clean.
          </strong>{' '}
          Getting that backwards is the single most common mistake we see, and
          the consequences show up within one wet season.
        </p>
        <div className="overflow-x-auto my-6" data-id="element-2154" data-mp="1u3ggq1rzrczc">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-2155" data-mp="1p4n5yh43a02v">
            <thead data-id="element-2156" data-mp="1p2isb7ge1mpe">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-2157" data-mp="1k625qhou7gef">
                <th className="py-2 pr-4 font-bold" data-id="element-2158" data-mp="1k84znct41tof" />
                <th className="py-2 pr-4 font-bold" data-id="element-2159" data-mp="1g500nk88oa4j">5/8" Minus</th>
                <th className="py-2 font-bold" data-id="element-2160" data-mp="1b5nfr50xhd4l">5/8" Clean</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-2161" data-mp="3airj998jq4c4">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2162" data-mp="35ivbr00n3urt">
                <td className="py-2 pr-4 font-semibold" data-id="element-2163" data-mp="3oo058mzlpa7z">Contains fines?</td>
                <td className="py-2 pr-4" data-id="element-2164" data-mp="3olx69dgh06r4">Yes — dust left in</td>
                <td className="py-2" data-id="element-2165" data-mp="3tg1fy4h4esvw">No — washed out</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2166" data-mp="3fnv24p1fwxzm">
                <td className="py-2 pr-4 font-semibold" data-id="element-2167" data-mp="3klwszw53y1ln">Compacts?</td>
                <td className="py-2 pr-4" data-id="element-2168" data-mp="3kk25ca2gbvdv">Yes, locks up hard</td>
                <td className="py-2" data-id="element-2169" data-mp="c7o7f8106b2z">No, stays loose</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2170" data-mp="g885cdtehiq6">
                <td className="py-2 pr-4 font-semibold" data-id="element-2171" data-mp="28fiitkzvmnh">Water passes through?</td>
                <td className="py-2 pr-4" data-id="element-2172" data-mp="2ajix1joq3j0">Barely — fines seal it</td>
                <td className="py-2" data-id="element-2173" data-mp="75p2vutj184g">Freely</td>
              </tr>
              <tr data-id="element-2174" data-mp="2my4w739yp2el">
                <td className="py-2 pr-4 font-semibold" data-id="element-2175" data-mp="2mxmqlg1etbu0">Order it for</td>
                <td className="py-2 pr-4" data-id="element-2176" data-mp="2ry0po8l19bfw">
                  Driveways, paths, base, mud control
                </td>
                <td className="py-2" data-id="element-2177" data-mp="2kcx69w2sn51j">
                  French drains, pipe bedding, dry creek beds
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2178" data-mp="2tewogrpjof62">
          What a Yard Weighs — and Why Your Pickup Cannot Carry One
        </h3>
        <p className="mb-4" data-id="element-2179" data-mp="20pz5snhf8gcs">
          A cubic yard of 5/8" Minus runs about 2,800 lbs, roughly 1.4 tons.
          Moisture moves that number a little; a yard that has been sitting in
          November rain is meaningfully heavier than the same yard in August.
        </p>
        <div className="overflow-x-auto my-6" data-id="element-2180" data-mp="2ahvfkvsvduwu">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-2181" data-mp="25otxkexd9f91">
            <thead data-id="element-2182" data-mp="1hz7zxvwn7835">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-2183" data-mp="1hypvibzttnk5">
                <th className="py-2 pr-4 font-bold" data-id="element-2184" data-mp="1d0oavmmr5g6k">Quantity</th>
                <th className="py-2 pr-4 font-bold" data-id="element-2185" data-mp="1d04vodohl5w8">Approx. weight</th>
                <th className="py-2 font-bold" data-id="element-2186" data-mp="1qogbffz1xoci">Typical job</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-2187" data-mp="1vmzyjjpdt66g">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2188" data-mp="20jqbhy5w3lnn">
                <td className="py-2 pr-4" data-id="element-2189" data-mp="25d2yk3ux7kea">1 yard</td>
                <td className="py-2 pr-4" data-id="element-2190" data-mp="29dmzliim785p">~2,800 lbs</td>
                <td className="py-2" data-id="element-2191" data-mp="29d3fpw9em9p1">Patching a few potholes</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2192" data-mp="2ebohw187v3g4">
                <td className="py-2 pr-4" data-id="element-2193" data-mp="2jb9i0ez4hhuf">4 yards</td>
                <td className="py-2 pr-4" data-id="element-2194" data-mp="2j9pz0gm30soy">~5.6 tons</td>
                <td className="py-2" data-id="element-2195" data-mp="2ob5m0e7tl99z">Topping off a short driveway</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2196" data-mp="oyb5rptv4b4g">
                <td className="py-2 pr-4" data-id="element-2197" data-mp="twuxy7z1uj59">12 yards (full load)</td>
                <td className="py-2 pr-4" data-id="element-2198" data-mp="tx4llowmge7s">~16.8 tons</td>
                <td className="py-2" data-id="element-2199" data-mp="377e4wx7zux8c">New driveway, ~10 × 50 ft at 6"</td>
              </tr>
              <tr data-id="element-2200" data-mp="32fw2s5u8iqls">
                <td className="py-2 pr-4" data-id="element-2201" data-mp="32fcwjmdy5qnl">Half-ton pickup</td>
                <td className="py-2 pr-4" data-id="element-2202" data-mp="2yeu5vvnsiqmz">1,000–1,500 lbs</td>
                <td className="py-2" data-id="element-2203" data-mp="2yf2gti0crlvj">
                  About 0.4 yd — <strong data-id="element-2204" data-mp="2tg9bicv7z16g">not</strong> a full yard
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4" data-id="element-2205" data-mp="3q5gq10xf8a0v">
          That last row catches people out constantly. A half-ton pickup is
          named for its payload rating, not its curb weight, and a single yard of
          minus is nearly double that rating. Loading a full yard into a
          half-ton overloads the suspension, ruins your braking distance, and is
          illegal on public roads. If you need one yard, take two trips, borrow a
          three-quarter ton, or have it delivered — our{' '}
          <Link to="/shop/5-8-minus" className="underline underline-offset-2" data-id="element-2206" data-mp="20qajq9pn4py3">
            5/8" Minus page
          </Link>{' '}
          will quote the delivered price to your address before you commit.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2207" data-mp="276sm0ak6hvjv">
          How Far a Yard Goes
        </h3>
        <p className="mb-4" data-id="element-2208" data-mp="1y6m8wpse6xl8">
          Coverage is entirely a function of depth. These are the numbers we use
          on the phone when someone reads us their dimensions:
        </p>
        <div className="overflow-x-auto my-6" data-id="element-2209" data-mp="1jfn4zs2i4x7z">
          <table className="w-full text-left text-sm [&_td]:pl-3 [&_th]:pl-3" data-id="element-2210" data-mp="1o8pxrxzgg2n4">
            <thead data-id="element-2211" data-mp="1ahbh3xt0sdj8">
              <tr className="border-b border-stone-300 dark:border-white/20" data-id="element-2212" data-mp="1ahuq2p0ozrl4">
                <th className="py-2 pr-4 font-bold" data-id="element-2213" data-mp="1eidfkcf44lk5">Depth</th>
                <th className="py-2 pr-4 font-bold" data-id="element-2214" data-mp="1egkah1k5e9e9">Coverage per yard</th>
                <th className="py-2 font-bold" data-id="element-2215" data-mp="10k6qnls55vwm">Use this depth for</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-stone-500/10 dark:[&>tr:nth-child(even)]:bg-white/[0.06]" data-id="element-2216" data-mp="15hfd3ykbom3o">
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2217" data-mp="ly9fc9p5qe6h">
                <td className="py-2 pr-4" data-id="element-2218" data-mp="gyn0jvtokpvk">2"</td>
                <td className="py-2 pr-4" data-id="element-2219" data-mp="vu30uikuti0e">~160 sq ft</td>
                <td className="py-2" data-id="element-2220" data-mp="vvnv0739ayxq">Refreshing an existing surface</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2221" data-mp="qtoqgtvs5vz7">
                <td className="py-2 pr-4" data-id="element-2222" data-mp="14tzhpy105ckw">3"</td>
                <td className="py-2 pr-4" data-id="element-2223" data-mp="14sof41jamavd">~100 sq ft</td>
                <td className="py-2" data-id="element-2224" data-mp="zuwl91z38eoj">Paths, mud control, top layer</td>
              </tr>
              <tr className="border-b border-stone-200 dark:border-white/10" data-id="element-2225" data-mp="1el4077pvzfkh">
                <td className="py-2 pr-4" data-id="element-2226" data-mp="19m0z1yd11ths">4"</td>
                <td className="py-2 pr-4" data-id="element-2227" data-mp="19m9dhuost6v3">~80 sq ft</td>
                <td className="py-2" data-id="element-2228" data-mp="1nl0rzi7koujr">Light-duty driveway over solid base</td>
              </tr>
              <tr data-id="element-2229" data-mp="1jkgu2cf6dmwk">
                <td className="py-2 pr-4" data-id="element-2230" data-mp="1jkyor4m6vt2d">6"</td>
                <td className="py-2 pr-4" data-id="element-2231" data-mp="1ji41z6fpdw5s">~54 sq ft</td>
                <td className="py-2" data-id="element-2232" data-mp="2py3gqapqo8nd">New driveway on soft ground</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4" data-id="element-2233" data-mp="2v02khlpe57ku">
          Add about 15% to whatever the math gives you. Compaction takes real
          volume out of a loose layer, and being a yard short at 4pm on a
          Saturday is a far worse outcome than having a wheelbarrow of rock left
          over. Our{' '}
          <Link to="/calculator" className="underline underline-offset-2" data-id="element-2234" data-mp="27a8978kpzr2p">
            yardage calculator
          </Link>{' '}
          already builds that allowance in.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2235" data-mp="2g7ilmx99xm97">
          Where It Belongs
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2236" data-mp="2l1mtrjua00bv">
          <li data-id="element-2237" data-mp="1oguijfo1i6sw">
            <strong data-id="element-2238" data-mp="1ohe3l58zlbb5">Residential driveways.</strong> Its main job. Four to six
            inches, crowned and compacted, handles daily car and light-truck
            traffic for years before it wants a fresh top layer.
          </li>
          <li data-id="element-2239" data-mp="1kxlyw38325q7">
            <strong data-id="element-2240" data-mp="1g1544q6dot45">Paths and walkways.</strong> A firm walking surface for a
            fraction of what pavers cost. Three inches over fabric is plenty.
          </li>
          <li data-id="element-2241" data-mp="1c1efas0ewn29">
            <strong data-id="element-2242" data-mp="170792xdx3vxp">Base under pavers, slabs, and walls.</strong> Compacts into
            something that carries weight evenly and does not settle the way
            soil does. Skip it and a patio goes wavy inside two seasons.
          </li>
          <li data-id="element-2243" data-mp="x9cixhxjwc6z">
            <strong data-id="element-2244" data-mp="x8uh85s4d5ro">Mud control.</strong> Gate areas, dog runs, side-yard
            parking. Three to four inches over fabric turns October-to-May soup
            into ground you can walk on in normal boots.
          </li>
          <li data-id="element-2245" data-mp="o7ml9ud2sh0r">
            <strong data-id="element-2246" data-mp="jamlg16tkai3">Backfill.</strong> Around foundations, behind retaining
            walls, over utility trenches. Compacts tight around irregular shapes
            without rocks large enough to damage pipe.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2247" data-mp="3il5catvp0x3i">
          Where It Does Not
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2248" data-mp="3ds2iqlqx1nnb">
          <li data-id="element-2249" data-mp="38thhqk3uazxt">
            <strong data-id="element-2250" data-mp="38v2bi7ibyeum">Anything that has to drain.</strong> French drains,
            downspout trenches, pipe bedding, dry creek beds. The fines are the
            enemy here — use{' '}
            <Link to="/shop/7-8-drain-rock" className="underline underline-offset-2" data-id="element-2251" data-mp="5dtwrwiwfv44">
              7/8" Drain Rock
            </Link>{' '}
            instead.
          </li>
          <li data-id="element-2252" data-mp="3rkicevxxg9bk">
            <strong data-id="element-2253" data-mp="3rk8ulvn1d8gy">Loaded-truck traffic.</strong> Dump trucks, equipment
            trailers, or a commercial approach want a coarser{' '}
            <Link to="/shop/1-25-minus" className="underline underline-offset-2" data-id="element-2254" data-mp="3mlfj28rb7rjf">
              1.25" Minus
            </Link>{' '}
            base under them.
          </li>
          <li data-id="element-2255" data-mp="2b5h1thlz9z74">
            <strong data-id="element-2256" data-mp="2b4z045gjqsrt">Soft clay with no base.</strong> Putting your wearing surface
            straight onto clay just pumps rock down into it. Build 4 inches of
            1.25" Minus first, then top with 5/8".
          </li>
          <li data-id="element-2257" data-mp="vlhs3ymv8hn4">
            <strong data-id="element-2258" data-mp="10mnqod37iz4k">Steep grades.</strong> Past roughly 15%, winter runoff walks
            the gravel downhill no matter how well you compacted it. That is a
            paving or chip-seal conversation.
          </li>
        </ul>

        <img src="https://cdn.magicpatterns.com/uploads/oevLQWcfQc8d828rBxgJqr/gravel_delivery_in_tacoma.png" alt="206Gravel truck delivering 5/8 minus gravel in a Tacoma alley" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2259" data-mp="1ad8o3x759xlw" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2260" data-mp="1jb2eysm53z1o">
          Installing It Properly
        </h3>
        <ol className="list-decimal pl-5 mb-4 space-y-3" data-id="element-2261" data-mp="3l4s1ftpyefa9">
          <li data-id="element-2262" data-mp="3l6cwdkg6k08n">
            <strong data-id="element-2263" data-mp="3l6uzn119feq2">Get the sub-grade firm and shaped first.</strong> Strip sod
            and soft organics, then grade the crown before any rock lands. You
            cannot fix a bowl-shaped sub-grade by burying it — the low spot
            stays a low spot and holds water under your new surface.
          </li>
          <li data-id="element-2264" data-mp="3s0tj002s7b75">
            <strong data-id="element-2265" data-mp="3s1d0xgsflzl6">Lay commercial-grade fabric.</strong> Not the thin weed
            barrier from the garden aisle. Overlap seams 6–12 inches. Fabric is
            what stops your rock from migrating down into the soil, which is the
            actual failure mode on most Puget Sound driveways.
          </li>
          <li data-id="element-2266" data-mp="tqs79quv6xg">
            <strong data-id="element-2267" data-mp="uaa4qgi9vbh">Spread in lifts, not one deep pile.</strong> Maximum 3–4
            inches per pass. A single 8-inch dump compacts on top and stays
            loose underneath, and it will rut the first time something heavy
            parks on it.
          </li>
          <li data-id="element-2268" data-mp="ojl2bjjsihe0">
            <strong data-id="element-2269" data-mp="olfnmyyz4bim">Build a real crown.</strong> Center about 2 inches higher
            than the edges across a 10-foot width. Water has to leave sideways;
            if it runs the length of the drive it cuts channels.
          </li>
          <li data-id="element-2270" data-mp="134yuxcpav6p2">
            <strong data-id="element-2271" data-mp="178oibyu8ni8i">Compact damp.</strong> A vibratory plate compactor, several
            passes per lift, with the material slightly moist. Bone-dry fines
            never bind; saturated fines turn to slurry. Damp is the window, and
            a garden hose on mist gets you there.
          </li>
        </ol>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2272" data-mp="jjcbh5mdvm9d">
          The Four Mistakes That Cost People a Second Delivery
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2273" data-mp="obx2jzdwnl97">
          <li data-id="element-2274" data-mp="aj76t38kr1yw">
            <strong data-id="element-2275" data-mp="ajp6yaycy0c3">Ordering clean instead of minus.</strong> The surface never
            firms up and tires push it into windrows within a month.
          </li>
          <li data-id="element-2276" data-mp="kht7mnpo3og">
            <strong data-id="element-2277" data-mp="jyeeetcxven">Skipping compaction entirely.</strong> Loose minus is just
            expensive gravel. Compaction is what converts it into a surface.
          </li>
          <li data-id="element-2278" data-mp="18u8lvpbxmgpn">
            <strong data-id="element-2279" data-mp="18uqhcjqrsqwi">Filling potholes without squaring them.</strong> Rock dumped
            into a smooth dish works straight back out. Cut the edges square
            first so the patch has something to key into.
          </li>
          <li data-id="element-2280" data-mp="z3498w9qb7xz">
            <strong data-id="element-2281" data-mp="z2kqjdc98fiw">Treating a water problem as a gravel problem.</strong> If
            street runoff or a downspout crosses the driveway, no amount of new
            rock holds. Fix the drainage, then re-rock.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2282" data-mp="l4w4w3ad54cs">
          What It Costs Delivered
        </h3>
        <p className="mb-4" data-id="element-2283" data-mp="g6mcg0g0whuv">
          5/8" Minus is $65 per yard. Delivery is a flat fee per truckload by
          zone — $80, $120, or $175 depending on distance — and it is the same
          fee whether you take 3 yards or a full 12. That is why partial loads
          are the expensive way to buy rock: a 12-yard load to Kent works out to
          about $72 per yard delivered, while a 4-yard load to the same address
          is closer to $85.
        </p>
        <p className="mb-4" data-id="element-2284" data-mp="2i2tt04exmx31">
          If you are within a load of a full truck, it is almost always worth
          rounding up. Enter your address on the{' '}
          <Link to="/shop/5-8-minus" className="underline underline-offset-2" data-id="element-2285" data-mp="2mulgu76peoph">
            product page
          </Link>{' '}
          and you will see the real delivered total before you order — or text
          dispatch your dimensions and we will work the yardage out with you.
        </p>
      </div>
}, {
  slug: 'best-time-regrade-gravel-driveway-seattle',
  title: 'August Is the Best Month to Regrade a Gravel Driveway in Seattle',
  date: 'August 1, 2026',
  excerpt: 'Puget Sound gets roughly six dry weeks a year, and August is the heart of them. Here is why late summer is the only window where crushed rock actually compacts — and what happens if you wait until the rain returns.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f693654-2a38-443b-b05e-cbf8adf96a42.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2286" data-mp="1fg82rm1ika29">
        <p className="mb-4" data-id="element-2287" data-mp="1tg0t3g94oo9p">
          Every fall we get the same call: a driveway in Shoreline or Renton
          that turned to soup after the first serious rain, and the homeowner
          wants gravel delivered tomorrow. We can absolutely deliver it. But the
          honest answer is that the repair would have cost less, gone faster,
          and lasted years longer if it had happened in August.
        </p>
        <p className="mb-4" data-id="element-2288" data-mp="1ahocj9fw0c43">
          Seattle averages under an inch of rain in August and close to six
          inches by November. That difference is not just a comfort issue for
          whoever is running the plate compactor — it decides whether your new
          rock locks together or floats.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2289" data-mp="dy7d9sd92wnb">
          Why Dry Ground Is the Whole Game
        </h3>
        <p className="mb-4" data-id="element-2290" data-mp="3th4p3nccua4">
          Crushed minus rock compacts because the stone dust in it fills the
          voids between the angular pieces and binds them into a near-solid
          slab. That only works within a fairly narrow moisture range. Too dry
          and the fines never bind; far more common around here, too wet and the
          sub-grade underneath turns plastic. The compactor pushes rock down
          into mud instead of knitting it together, and you have effectively
          buried a few hundred dollars of material.
        </p>
        <p className="mb-4" data-id="element-2291" data-mp="cmu5errnd06r">
          In August the native glacial till under most Puget Sound driveways is
          as firm as it gets all year. You compact onto something solid, the
          layers stay where you put them, and the finished surface has two full
          months of dry weather to settle under traffic before the rain tests
          it.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2292" data-mp="2jmy31yw85ilb">
          What a Late-Summer Regrade Actually Involves
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2293" data-mp="2epenmvnsizrs">
          <li data-id="element-2294" data-mp="29nh0vrg6lmu3">
            <strong data-id="element-2295" data-mp="29p0f7cia1nt8">Re-establish the crown.</strong> The center should sit
            roughly 2 inches higher than the edges over a 10-foot width so water
            sheds sideways instead of running the length of the drive and
            cutting channels.
          </li>
          <li data-id="element-2296" data-mp="2xdjmppti5wk9">
            <strong data-id="element-2297" data-mp="2xeur9rurv8cf">Fix the potholes properly.</strong> Square up the edges of
            each hole rather than just filling the dish — loose rock dumped into
            a smooth bowl works right back out within a season.
          </li>
          <li data-id="element-2298" data-mp="2ojf05ilej7aj">
            <strong data-id="element-2299" data-mp="3l3dxm1u9hb1v">Top with 5/8" Minus.</strong> Two to three inches of fresh
            surface rock, compacted in a single pass, restores the driving
            surface without rebuilding the base.
          </li>
          <li data-id="element-2300" data-mp="12ub3j8uzlf4u">
            <strong data-id="element-2301" data-mp="12ut8ctvpt1od">Check where the water goes.</strong> If runoff from the
            street or a downspout crosses the driveway, no amount of new rock
            will hold. That is a drainage fix, not a gravel fix.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2302" data-mp="1bwu1bejx11k8">
          If the Base Is Gone, Do It Now
        </h3>
        <p className="mb-4" data-id="element-2303" data-mp="1ltf8o6jeqor8">
          Surface rutting is a topcoat problem. But if you can feel the driveway
          flex under a vehicle, or ruts return within weeks of being filled, the
          structural base has failed and topping it is throwing good money
          after bad. Rebuilding means 3–4 inches of 1.25" Minus compacted as a
          base, then the 5/8" Minus surface over it. That is a job you want on
          dry ground — attempting it in November roughly doubles the material it
          takes to reach a stable grade.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2304" data-mp="29gdnkzrvpon1">
          One Local Wrinkle: Drainage Rock Season
        </h3>
        <p className="mb-4" data-id="element-2305" data-mp="3esyyawynec0l">
          August is also the practical deadline for French drains and downspout
          trenches. Digging is dramatically easier in dry soil, and a drain
          installed now is proven before the wet season instead of during it. If
          your driveway problem is really a water problem, 7/8" Drain Rock in a
          properly fabric-wrapped trench will do more for it than any amount of
          surface gravel.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2306" data-mp="3jrtgbwosjwjn">
          The Short Version
        </h3>
        <p className="mb-4" data-id="element-2307" data-mp="3oq3coam9536u">
          You have until roughly late September before Puget Sound weather stops
          cooperating. A driveway regraded in August compacts properly, settles
          under two months of dry traffic, and heads into winter with a crown
          that actually sheds water. The same work in November costs more rock
          and delivers a worse result. If you are on the fence, this is the
          month to get it done — call or text dispatch and we will help you
          figure out the yardage before you order.
        </p>
      </div>
}, {
  slug: 'walkway-gravel-vs-driveway-gravel',
  title: 'Walkway Gravel vs Driveway Gravel — Which One Do I Need?',
  date: 'March 3, 2026',
  excerpt: 'They look similar in the bin, but walkway gravel and driveway gravel are built for very different jobs. Here is how to pick the right one so your project lasts.',
  image: 'https://cdn.magicpatterns.com/uploads/ebxsCGdnf9doSY72o8VZYu/file_0000000013d071fd98aa813ad564b1e6.png',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2308" data-mp="gf9a8it9rf2w">
        <p className="mb-4" data-id="element-2309" data-mp="1wtiylcxih3tq">
          Walkway gravel and driveway gravel can look almost identical sitting
          in a bulk bin, but they are engineered for completely different loads.
          A path only has to carry foot traffic; a driveway has to hold up under
          the weight of cars, trucks, and the occasional delivery vehicle.
          Choosing the wrong one means a soft, rutted path or a driveway that
          scatters every time you back out. Here is how to tell them apart.
        </p>

        <img src="https://cdn.magicpatterns.com/uploads/wghDVQbBxatajKiUKq9jcv/driveway_gravel_spreading_in_tacoma.png" alt="Two workers raking crushed gravel behind a 206Gravel delivery truck on a driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2310" data-mp="2dudlvkfzq7xz" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2311" data-mp="1q63um300crzg">
          Walkway Gravel: Comfort & Looks
        </h3>
        <p className="mb-4" data-id="element-2312" data-mp="xi7zh2q3784s">
          Walkways prioritize a comfortable surface to walk on and a clean,
          finished appearance. Smaller, rounded, or decorative stone — like 3/8"
          pea gravel or crushed drain rock — feels good underfoot and packs into
          a stable, attractive path. Because a walkway never sees vehicle
          weight, you do not need a heavy structural base; a few inches of
          material over a compacted, weed-blocked sub-grade is usually plenty.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2313" data-mp="g0j6rsosp4gs">
          <li data-id="element-2314" data-mp="fzzpme6yyk3t">
            <strong data-id="element-2315" data-mp="g2lych5lj5nm">Stone size:</strong> 3/8" pea gravel or small decorative
            rock — smooth and easy on bare feet.
          </li>
          <li data-id="element-2316" data-mp="77o2b175jcs2">
            <strong data-id="element-2317" data-mp="77xvsyreo22i">Depth:</strong> 2–3 inches over landscape fabric.
          </li>
          <li data-id="element-2318" data-mp="28l68695ggw8">
            <strong data-id="element-2319" data-mp="2a62c0b447w7">Best for:</strong> garden paths, side yards, patio borders,
            and fire-pit surrounds.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png" alt="Landscape fabric laid down before a gravel walkway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2320" data-mp="3oguhyzq54m3n" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2321" data-mp="d0j3wa3cr7xw">
          Driveway Gravel: Strength & Stability
        </h3>
        <p className="mb-4" data-id="element-2322" data-mp="47xmoyfdd9b6">
          Driveways need angular, "minus" crushed rock that locks together under
          weight. The fines (stone dust) in a minus product bind the larger
          stones into a firm, near-solid surface once compacted — exactly what
          you want when a vehicle rolls over it. A driveway is built in layers:
          a coarse 1.25" Minus base for structure, topped with a smoother 5/8"
          Minus driving surface that sheds water and resists potholes.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2323" data-mp="3lfjdnnyq5twt">
          <li data-id="element-2324" data-mp="3lg2sgvt2w26m">
            <strong data-id="element-2325" data-mp="3lhm9wpqh0j9z">Base layer:</strong> 1.25" Minus crushed rock, 3–4 inches,
            compacted.
          </li>
          <li data-id="element-2326" data-mp="1jog12t59y59m">
            <strong data-id="element-2327" data-mp="1joy2s5aphbox">Top layer:</strong> 5/8" Minus crushed rock, 2–3 inches, the
            finished driving surface.
          </li>
          <li data-id="element-2328" data-mp="3479x3o1kbtih">
            <strong data-id="element-2329" data-mp="346qfy9jql95i">Best for:</strong> driveways, parking pads, and any surface
            that carries vehicles.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png" alt="Finished compacted gravel driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-2330" data-mp="3dx1ueq11r0qn" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2331" data-mp="3nwkc0zthiafj">
          Quick Answer
        </h3>
        <p className="mb-4" data-id="element-2332" data-mp="phznu4nqhcw">
          If people walk on it, choose a smaller, comfortable stone like 3/8"
          pea gravel. If vehicles drive on it, use angular minus crushed rock in
          two compacted layers. When in doubt, give us a call — we will help you
          pick the right material and the right amount before you order.
        </p>
      </div>
}, {
  slug: 'perfect-gravel-driveway',
  title: 'How to Build a Perfect Gravel Driveway',
  date: 'January 14, 2026',
  excerpt: 'A complete step-by-step guide to transforming a dirt driveway into a professional, long-lasting gravel surface. No contractor needed.',
  image: 'https://cdn.magicpatterns.com/uploads/bH95N6JcLS3ddKehbnVRWv/gravel_landscaping_in_tacoma.png',
  type: 'guide',
  quickInfo: ['⏱ Weekend project', '📏 Any driveway size', '💰 ~$3–5/sq ft', '🔧 Basic tools needed'],
  materialsNeeded: [{
    name: '1.25" Minus Crushed Rock',
    use: 'Base layer — compacts into a solid foundation',
    slug: '1-25-minus'
  }, {
    name: '5/8" Minus Crushed Rock',
    use: 'Top layer — smooth, firm driving surface',
    slug: '5-8-minus'
  }, {
    name: 'Landscape Fabric',
    use: 'Weed barrier between soil and gravel',
    slug: null
  }, {
    name: 'Fill Dirt (if needed)',
    use: 'Level out low spots before gravel',
    slug: null
  }],
  steps: [{
    number: 1,
    title: 'Assess & Measure Your Driveway',
    image: 'https://cdn.magicpatterns.com/uploads/2ebE9ciuiVZZKZHNZLJiZ5/measuring_gravel_driveway.png',
    description: 'Start by measuring the length, width, and noting the current condition of your dirt driveway. Mark the boundaries with stakes and string. Calculate the square footage — this determines how much material you need.',
    tips: ['Measure length × width in feet', 'Plan for 4–6 inches of base material depth', 'Use our Calculator to estimate yards needed', 'Account for any slope or drainage needs']
  }, {
    number: 2,
    title: 'Clear & Grade the Surface',
    image: 'https://cdn.magicpatterns.com/uploads/gQYsAvdSYDiWUkNHGFHmQH/grading_driveway.png',
    description: 'Remove any vegetation, large rocks, and debris from the driveway area. Use a rake or skid steer to grade the surface so water drains to the sides. A slight crown (higher in the center) prevents puddles.',
    tips: ['Remove all grass, weeds, and roots', 'Create a 2% slope from center to edges', 'Fill any low spots with fill dirt', 'Compact the bare soil with a plate compactor']
  }, {
    number: 3,
    title: 'Install Landscape Fabric (Optional)',
    image: 'https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png',
    description: 'Lay commercial-grade landscape fabric over the graded dirt. This prevents weeds from growing up through your gravel and helps separate the soil from the rock layers. Overlap seams by 6–12 inches.',
    tips: ['Use commercial-grade fabric, not cheap weed barrier', 'Overlap seams by at least 6 inches', 'Secure with landscape staples every 3 feet', 'This step saves major headaches down the road']
  }, {
    number: 4,
    title: 'Spread the Base Layer — 1.25" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/wghDVQbBxatajKiUKq9jcv/driveway_gravel_spreading_in_tacoma.png',
    description: 'The base layer is the foundation of your driveway. Spread 3–4 inches of 1.25" Minus crushed rock evenly across the surface. This coarser material locks together and creates a rock-solid base that won\'t shift.',
    tips: ['Order 1.25" Minus from our catalog', 'Spread evenly with a rake to 3–4" depth', 'Compact thoroughly with a plate compactor', 'Make multiple passes until the surface is firm'],
    material: '1.25" Minus',
    materialSlug: '1-25-minus'
  }, {
    number: 5,
    title: 'Compact the Base Layer',
    image: 'https://cdn.magicpatterns.com/uploads/hkbT29dFvc9BT49e5MH1GV/compacting_base_layer.png',
    description: 'Rent a plate compactor (available at most equipment rental shops) and make several passes over the base layer. Wet the material lightly before compacting — moisture helps the fines bind together for maximum density.',
    tips: ['Lightly wet the gravel before compacting', 'Make 3–4 passes in different directions', 'The surface should feel solid underfoot', 'Add more material to any low spots and re-compact']
  }, {
    number: 6,
    title: 'Add the Top Layer — 5/8" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/pqds5pqkfBofDzHJ4pjSBj/base_layer_compacting.png',
    description: 'Now spread 2–3 inches of 5/8" Minus crushed rock on top. This is the finished driving surface — it packs down tight, sheds water, and provides a smooth, firm surface for vehicles and foot traffic.',
    tips: ['Order 5/8" Minus — our most popular driveway gravel', 'Spread to 2–3" depth over the compacted base', 'Rake smooth and maintain the crown shape', 'Compact one final time for a professional finish'],
    material: '5/8" Minus',
    materialSlug: '5-8-minus'
  }, {
    number: 7,
    title: 'Final Compaction & Edging',
    image: 'https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png',
    description: 'Give the top layer a final compaction pass. Optionally, install edging (timber, steel, or stone) along the sides to keep gravel contained. Your new driveway is ready to use — it will continue to settle and harden over the first few weeks.',
    tips: ['Edging prevents gravel from migrating into the yard', 'Drive on it gently for the first week', 'Top off with a thin layer of 5/8" Minus annually', 'Keep drainage channels clear of debris']
  }],
  seoBlocks: [{
    title: 'Gravel Delivery Across Pierce & King Counties',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-2333" data-mp="jl73oupfvxtv">
              Whether you're building a new gravel driveway in Puyallup,
              replacing a muddy parking pad in Bonney Lake, or resurfacing a
              rural road in Enumclaw, having the right materials delivered on
              time makes all the difference. 206Gravel provides same-day and
              next-day bulk gravel delivery from Tacoma to Seattle and
              everywhere in between — Lakewood, Federal Way, Auburn, Kent,
              Sumner, Edgewood, Renton, Bellevue, Kirkland, Redmond, and the
              surrounding Pierce and King County communities.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-2334" data-mp="1sns5ygbqsfog">
              Our dump trucks carry 5–12 yards per load and can place material
              directly on your driveway, jobsite, or backyard — no wheelbarrow
              relay required. We know the local soil conditions (hello, Puget
              Sound clay) and can recommend the right base and top layers for
              driveways that last through Pacific Northwest winters.
            </p>
          </>
  }, {
    title: 'Why Buy Gravel Locally in the Puget Sound?',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-2335" data-mp="22k501fb78vjq">
              Big-box stores sell gravel by the bag — but for driveways and
              serious landscaping, you need bulk delivery by the yard. Buying
              locally from 206Gravel means lower delivery costs, fresher
              material sourced from regional quarries, and drivers who know how
              to navigate Tacoma's hills, Seattle's tight streets, King County's
              rural roads, and everything in between.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-2336" data-mp="32mjv1wsed9nl">
              We're based in the 206 area code and deliver to King County and
              King County daily. No middlemen, no markups from national chains —
              just honest pricing on crushed rock, drain rock, topsoil, bark,
              and recycled aggregates delivered straight to your property.
            </p>
          </>
  }]
}, {
  slug: 'best-base-for-gravel-driveway',
  title: 'Best Base for a Gravel Driveway',
  date: 'November 18, 2025',
  excerpt: 'The best base for a gravel driveway is a large angular crushed stone (around 1 to 2 inches) for the sub-base, laid...',
  image: 'https://cdn.magicpatterns.com/uploads/8BfgzDv9jVyH7TX3BiStiF/Gravel2BDriveway2BHero2BDesktop.webp',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2337" data-mp="15tb8vjm6wvjr">
        <p className="mb-4" data-id="element-2338" data-mp="10uq9fmerifwd">
          The foundation of any long-lasting gravel driveway is its base layer.
          Without a proper base, your top gravel will sink into the mud,
          creating potholes and requiring constant maintenance.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2339" data-mp="1al2xvppp6t7s">
          Why 1.25" Minus is the Standard
        </h3>
        <p className="mb-4" data-id="element-2340" data-mp="hw5hjs51r6ho">
          For most residential driveways in the Pacific Northwest, 1.25" Minus
          crushed rock is the go-to base material. The "minus" means it contains
          stone dust (fines) along with the larger 1.25-inch angular rocks. When
          compacted, these fines lock the larger stones together like concrete.
        </p>
        <p className="mb-4" data-id="element-2341" data-mp="27cn0z9s5xao8">
          Always ensure you lay down at least 3-4 inches of base rock and
          compact it thoroughly before adding your top layer.
        </p>
      </div>
}, {
  slug: 'how-much-is-a-load-of-gravel',
  title: 'How Much is a Load of Gravel?',
  date: 'October 2, 2025',
  excerpt: 'A standard tandem-axle dump truck load of gravel (around 12 to 15 tons) costs around $400 to $1,000 delivered across the Puget Sound area...',
  image: 'https://cdn.magicpatterns.com/uploads/pPaGG3aiENovvC8wi26rwz/TJHunt5-700x467.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-2342" data-mp="2tjhen09a5ems">
        <p className="mb-4" data-id="element-2343" data-mp="2yhiwjhuyz7wo">
          The cost of a load of gravel depends heavily on the material type,
          your location, and the size of the truck delivering it.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-2344" data-mp="2or7iv3lhhkcl">
          Truck Sizes
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-2345" data-mp="3cgil6pihfmsu">
          <li data-id="element-2346" data-mp="3h91vndlvi1tf">
            <strong data-id="element-2347" data-mp="3h9lfx0yzx2am">Solo Dump Truck:</strong> Carries about 5 to 7 yards (6-9
            tons). Ideal for smaller residential projects.
          </li>
          <li data-id="element-2348" data-mp="3r266ie0f8xda">
            <strong data-id="element-2349" data-mp="3m5p7upvlj0ly">Tandem-Axle Dump Truck:</strong> Carries about 10 to 12
            yards (12-15 tons). The standard for most driveway installations.
          </li>
          <li data-id="element-2350" data-mp="3h8fljj3i752c">
            <strong data-id="element-2351" data-mp="3h8755h869hme">Truck and Pup:</strong> Carries up to 24 yards. Best for
            large rural driveways or commercial jobs.
          </li>
        </ul>
        <p className="mb-4" data-id="element-2352" data-mp="386g2kl8xhqwr">
          Remember that delivery fees are often a significant portion of the
          total cost, which is why buying locally from a supplier in your
          delivery zone is crucial.
        </p>
      </div>
}];